import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const psychologyUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Development and Learning",
  examWeight: "15–25%",
  focus: "How biological, cognitive, and environmental factors interact to influence growth and behavior modification.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Themes and Methods in Developmental Psychology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Enduring Themes</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Developmental psychology examines biological, physical, psychological, and behavioral changes that occur throughout life.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Nature vs. Nurture</h4>
                <p className="text-sm text-slate-700 mt-2">How much of our development is the result of genetic inheritance (nature) versus environmental experience (nurture)? Modern psychology emphasizes the <strong>interaction</strong> of both (epigenetics).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Continuity vs. Discontinuity</h4>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Continuity</p>
                    <p className="text-xs text-slate-600">Development is a gradual, cumulative process (e.g., a sapling growing into a tree). Usually associated with experience and learning.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Discontinuity</p>
                    <p className="text-xs text-slate-600">Development proceeds in distinct stages or steps (e.g., a caterpillar becoming a butterfly). Usually associated with biological maturation (e.g., Piaget, Erikson).</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Stability vs. Change</h4>
                <p className="text-sm text-slate-700 mt-2">Do our early personality traits persist through life, or do we become different persons as we age? (e.g., temperament tends to be stable; social attitudes may change).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Research Methods in Development</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Cross-Sectional Design</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Definition:</strong> Researchers compare people of different ages at the same point in time.</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-blue-700"><strong>Pros:</strong> Fast, inexpensive, no attrition (participants dropping out).</p>
                  <p className="text-xs text-blue-700"><strong>Cons:</strong> Susceptible to <strong>Cohort Effects</strong> (differences due to cultural/historical experiences of a generation, not age itself).</p>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Longitudinal Design</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Definition:</strong> Researchers follow the <strong>same</strong> group of people (a cohort) over a long period.</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-purple-700"><strong>Pros:</strong> Accurately measures developmental changes; eliminates cohort effects.</p>
                  <p className="text-xs text-purple-700"><strong>Cons:</strong> Expensive, time-consuming, high attrition rates.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Physical Development Across the Lifespan",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Prenatal Development</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              While the specific stages (zygote, embryo, fetus) are excluded from the exam, the <em>influences</em> on them are tested.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Teratogens" 
                definition="Chemicals or viruses that can reach the embryo or fetus during prenatal development and cause harm."
              />
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Examples of Teratogens</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Alcohol:</strong> Can cause Fetal Alcohol Syndrome (FAS), leading to physical abnormalities and cognitive impairments.</li>
                  <li><strong>Viruses/Bacteria:</strong> Rubella, HIV, etc.</li>
                  <li><strong>Genetic Factors:</strong> Abnormalities like Down Syndrome (Trisomy 21).</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Infancy and Childhood</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Maturation" 
                definition="Biological growth processes that enable orderly changes in behavior, relatively uninfluenced by experience (e.g., standing before walking)."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Reflexes</h4>
                <p className="text-sm text-slate-700 mt-2">Innate, automatic responses illustrating healthy neural development.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Rooting:</strong> Turning head when cheek is touched (to nurse).</li>
                  <li><strong>Moro:</strong> Startle reflex (arms fling out).</li>
                  <li><strong>Babinski:</strong> Toes fan out when the sole of the foot is touched.</li>
                </ul>
              </div>
              <KeyTerm 
                term="Motor Development" 
                definition="Generally universal in sequence (Roll → Sit → Crawl → Walk) but timing varies."
              />
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Visual Cliff (Gibson & Walk)</h4>
                <p className="text-sm text-slate-700 mt-2">An experiment demonstrating that depth perception is largely innate or learned very early (infants refused to crawl over a "cliff" of glass).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Critical Periods</h4>
                <p className="text-sm text-slate-700 mt-2">An optimal period shortly after birth when an organism's exposure to certain stimuli or experiences produces proper development (e.g., language, vision).</p>
                <p className="text-xs text-purple-700 mt-2"><strong>Imprinting (Konrad Lorenz):</strong> Certain birds form strong attachments during a critical period very early in life.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Adolescence</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Puberty</h4>
                <p className="text-sm text-slate-700 mt-2">The period of sexual maturation.</p>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Primary Sex Characteristics</p>
                    <p className="text-xs text-slate-600">Structures making reproduction possible (ovaries, testes).</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Secondary Sex Characteristics</p>
                    <p className="text-xs text-slate-600">Non-reproductive sexual traits (breasts, hips, voice quality, body hair).</p>
                  </div>
                </div>
              </div>
              <KeyTerm 
                term="Menarche & Spermarche" 
                definition="Menarche = first menstrual period. Spermarche = first ejaculation."
              />
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">Brain Development</h4>
                <p className="text-sm text-slate-700 mt-2">The <strong>Frontal Lobe</strong> (judgment, impulse control) lags behind the <strong>Limbic System</strong> (emotion, reward), explaining risky adolescent behavior. Pruning of unused neurons occurs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Adulthood and Aging</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Physical Decline" 
                definition="Gradual decline in fertility (Menopause in women), visual acuity, muscle mass, and immune system function."
              />
              <KeyTerm 
                term="The Aging Brain" 
                definition="Slower neural processing; however, exercise and mental stimulation can promote neurogenesis."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Gender and Sexual Orientation",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <KeyTerm 
                term="Sex" 
                definition="Biologically determined status (chromosomes/anatomy)."
              />
              <KeyTerm 
                term="Gender" 
                definition="The socially constructed roles and characteristics by which a culture defines male and female."
              />
              <KeyTerm 
                term="Gender Roles" 
                definition="Social expectations about how men and women should behave."
              />
              <KeyTerm 
                term="Gender Identity" 
                definition="One's sense of being male, female, or somewhere on the spectrum."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Social Learning Theory</h4>
                <p className="text-sm text-slate-700 mt-2">We learn social behavior (gender roles) by observing and imitating and by being rewarded or punished.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Gender Schema Theory</h4>
                <p className="text-sm text-slate-700 mt-2">Children form mental concepts (schemas) of gender and adjust their behavior to fit these concepts.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Cognitive Development Across the Lifespan",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Jean Piaget's Theory of Cognitive Development</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Piaget believed children are active thinkers who construct understanding through schemas.
            </p>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Schemas" 
                definition="Mental molds into which we pour our experiences."
              />
              <KeyTerm 
                term="Assimilation" 
                definition="Interpreting new experiences in terms of existing schemas (e.g., seeing a cat and calling it a 'dog')."
              />
              <KeyTerm 
                term="Accommodation" 
                definition="Adapting current schemas to incorporate new information (e.g., realizing cats are different from dogs)."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Piaget's Four Stages:</h4>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">1. Sensorimotor Stage (Birth–2 years)</h5>
                <p className="text-sm text-slate-700 mt-2">Experiencing the world through senses and actions.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Key Milestone:</strong> Object Permanence (awareness that things continue to exist even when not perceived).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">2. Preoperational Stage (2–7 years)</h5>
                <p className="text-sm text-slate-700 mt-2">Representing things with words and images; using intuitive rather than logical reasoning.</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-slate-700 mt-2">
                  <li><strong>Egocentrism:</strong> Difficulty taking another's point of view.</li>
                  <li><strong>Animism:</strong> Believing inanimate objects have feelings.</li>
                  <li><strong>Lack of Conservation:</strong> Inability to understand that mass/volume remains the same despite changes in shape.</li>
                  <li><strong>Theory of Mind:</strong> People's ideas about their own and others' mental states (begins to develop here).</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">3. Concrete Operational Stage (7–11 years)</h5>
                <p className="text-sm text-slate-700 mt-2">Thinking logically about concrete events.</p>
                <p className="text-xs text-green-700 mt-2"><strong>Key Milestone:</strong> Grasping Conservation and mathematical transformations (reversibility).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">4. Formal Operational Stage (12–Adulthood)</h5>
                <p className="text-sm text-slate-700 mt-2">Abstract reasoning.</p>
                <p className="text-xs text-orange-700 mt-2"><strong>Key Milestone:</strong> Abstract logic, potential for mature moral reasoning.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Lev Vygotsky's Sociocultural Theory</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Unlike Piaget, Vygotsky emphasized how the child's mind grows through interaction with the social environment.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Zone of Proximal Development (ZPD)" 
                definition="The zone between what a child can do alone and what they can do with help."
              />
              <KeyTerm 
                term="Scaffolding" 
                definition="The support given by a teacher/parent to help a learner master a task."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Cognition in Adulthood</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Fluid Intelligence</h4>
                <p className="text-sm text-slate-700 mt-2">Ability to reason speedily and abstractly; tends to <strong>decrease</strong> with late adulthood.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Crystallized Intelligence</h4>
                <p className="text-sm text-slate-700 mt-2">Accumulated knowledge and verbal skills; tends to <strong>increase</strong> or stabilize with age.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Communication and Language Development",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Structure of Language</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Phonemes" 
                definition="Smallest distinctive sound units (e.g., 'ch', 'a', 't')."
              />
              <KeyTerm 
                term="Morphemes" 
                definition="Smallest units that carry meaning (e.g., 'pre', 'view', 'ed')."
              />
              <KeyTerm 
                term="Grammar" 
                definition="System of rules (Semantics = meaning; Syntax = order)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Stages of Language Development</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">1. Babbling Stage (4 months)</h5>
                <p className="text-sm text-slate-700 mt-1">Spontaneous sounds.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">2. One-Word Stage (1 year)</h5>
                <p className="text-sm text-slate-700 mt-1">"Holophrase" (e.g., "Doggy").</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">3. Two-Word Stage (2 years)</h5>
                <p className="text-sm text-slate-700 mt-1">"Telegraphic speech" (e.g., "Go car").</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Overgeneralization</h5>
                <p className="text-sm text-slate-700 mt-1">Applying grammatical rules too broadly (e.g., "I goed to the store").</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Theories of Language Acquisition</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Noam Chomsky (Nativist)</h4>
                <p className="text-sm text-slate-700 mt-2">Humans have an inborn <strong>Language Acquisition Device (LAD)</strong> and Universal Grammar. We are pre-wired to learn language.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">B.F. Skinner (Behaviorist)</h4>
                <p className="text-sm text-slate-700 mt-2">Language is learned through association, imitation, and reinforcement (operant conditioning).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Critical Period Hypothesis</h4>
                <p className="text-sm text-slate-700 mt-2">If language is not exposed by age 7, ability to master any language diminishes (e.g., case of Genie).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Social-Emotional Development Across the Lifespan",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Attachment Theory</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Harry Harlow</h4>
                <p className="text-sm text-slate-700 mt-2">Studied rhesus monkeys. Found that infants preferred <strong>Contact Comfort</strong> (cloth mother) over nutrition (wire mother).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Konrad Lorenz</h4>
                <p className="text-sm text-slate-700 mt-2">Studied <strong>Imprinting</strong> in birds (attaching to the first moving object seen).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Mary Ainsworth (Strange Situation)</h4>
                <p className="text-sm text-slate-700 mt-2">Observed infant reactions when mom left and returned.</p>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Secure Attachment</p>
                    <p className="text-xs text-slate-600">Distress when mom leaves, happy when she returns. (Result of responsive parenting).</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Insecure Avoidant</p>
                    <p className="text-xs text-slate-600">No distress when mom leaves, ignores her upon return.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Insecure Anxious/Ambivalent</p>
                    <p className="text-xs text-slate-600">Intense distress, difficult to soothe upon return.</p>
                  </div>
                </div>
              </div>
              <KeyTerm 
                term="Temperament" 
                definition="An infant's biological/genetic emotional reactivity and intensity (e.g., 'easy' vs. 'difficult' babies)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Parenting Styles (Diana Baumrind)</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Authoritarian</h4>
                <p className="text-sm text-slate-700 mt-2">Strict rules, obedience expected, low warmth ("Because I said so").</p>
                <p className="text-xs text-red-700 mt-2">→ Children may have lower self-esteem/social skills.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Permissive</h4>
                <p className="text-sm text-slate-700 mt-2">High warmth, few rules, little punishment.</p>
                <p className="text-xs text-orange-700 mt-2">→ Children may be aggressive or immature.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Authoritative ⭐</h4>
                <p className="text-sm text-slate-700 mt-2">Demanding but responsive; sets rules but explains reasons; encourages discussion.</p>
                <p className="text-xs text-green-700 mt-2">→ Associated with highest self-esteem, self-reliance, and social competence.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Neglectful/Uninvolved</h4>
                <p className="text-sm text-slate-700 mt-2">Low warmth, low control.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Erik Erikson's Psychosocial Stages</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Development is a series of social conflicts/crises that must be resolved.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-blue-800">Trust vs. Mistrust (Infancy)</h4>
                  <p className="text-sm text-slate-600">Needs met → trust.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-purple-800">Autonomy vs. Shame/Doubt (Toddler)</h4>
                  <p className="text-sm text-slate-600">Doing things for themselves.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-green-800">Initiative vs. Guilt (Preschool)</h4>
                  <p className="text-sm text-slate-600">Initiating tasks/play.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <span className="bg-orange-200 text-orange-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-orange-800">Industry vs. Inferiority (Elem. School)</h4>
                  <p className="text-sm text-slate-600">Feeling competent/productive.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg border border-pink-200">
                <span className="bg-pink-200 text-pink-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-pink-800">Identity vs. Role Confusion (Adolescence)</h4>
                  <p className="text-sm text-slate-600">Refining sense of self.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <span className="bg-cyan-200 text-cyan-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">6</span>
                <div>
                  <h4 className="font-semibold text-cyan-800">Intimacy vs. Isolation (Young Adult)</h4>
                  <p className="text-sm text-slate-600">Forming close relationships.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <span className="bg-indigo-200 text-indigo-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">7</span>
                <div>
                  <h4 className="font-semibold text-indigo-800">Generativity vs. Stagnation (Middle Adult)</h4>
                  <p className="text-sm text-slate-600">Contributing to the world/family.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                <span className="bg-violet-200 text-violet-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">8</span>
                <div>
                  <h4 className="font-semibold text-violet-800">Integrity vs. Despair (Late Adult)</h4>
                  <p className="text-sm text-slate-600">Reflection on life satisfaction.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Adolescent Social Development</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Identity Statuses (James Marcia)</h4>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Foreclosure</p>
                    <p className="text-xs text-slate-600">Commitment made without exploring alternatives (parents' choice).</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Moratorium</p>
                    <p className="text-xs text-slate-600">Exploring but no commitment yet.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Diffusion</p>
                    <p className="text-xs text-slate-600">No exploration, no commitment.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Achievement</p>
                    <p className="text-xs text-slate-600">Explored and committed.</p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Adolescent Egocentrism (David Elkind)</h4>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Imaginary Audience</p>
                    <p className="text-xs text-slate-600">Belief that everyone is watching them.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Personal Fable</p>
                    <p className="text-xs text-slate-600">Belief that they are unique and invincible.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Classical Conditioning",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed italic">Learning by association. A neutral stimulus becomes associated with a meaningful stimulus and acquires the capacity to elicit a similar response.</p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Key Figures</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Ivan Pavlov" 
                definition="Discovered classical conditioning with dogs (salivation)."
              />
              <KeyTerm 
                term="John B. Watson" 
                definition="Applied it to humans (Little Albert); founded Behaviorism."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Components</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Unconditioned Stimulus (UCS)" 
                definition="Stimulus that naturally triggers a response (e.g., Food)."
              />
              <KeyTerm 
                term="Unconditioned Response (UCR)" 
                definition="Unlearned, natural response (e.g., Salivation to food)."
              />
              <KeyTerm 
                term="Neutral Stimulus (NS)" 
                definition="Stimulus that elicits no response before conditioning (e.g., Bell)."
              />
              <KeyTerm 
                term="Conditioned Stimulus (CS)" 
                definition="Previously neutral stimulus that, after association with UCS, triggers a response (e.g., Bell after training)."
              />
              <KeyTerm 
                term="Conditioned Response (CR)" 
                definition="Learned response to the CS (e.g., Salivation to bell)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Processes</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Acquisition" 
                definition="The initial stage of linking NS and UCS. Timing matters (NS should come just before UCS)."
              />
              <KeyTerm 
                term="Extinction" 
                definition="Diminishing of CR when CS occurs repeatedly without UCS."
              />
              <KeyTerm 
                term="Spontaneous Recovery" 
                definition="Reappearance of an extinguished CR after a pause."
              />
              <KeyTerm 
                term="Generalization" 
                definition="Responding to stimuli similar to the CS (e.g., Little Albert fearing all white furry things)."
              />
              <KeyTerm 
                term="Discrimination" 
                definition="Ability to distinguish between CS and other irrelevant stimuli."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Biological Constraints</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-bold text-orange-800">Garcia Effect (Taste Aversion)</h4>
              <p className="text-sm text-slate-700 mt-2">We are biologically predisposed to learn associations that aid survival. Taste is easily associated with nausea (even with a long time delay), but sights/sounds are not.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: Operant Conditioning",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed italic">Learning in which behavior is strengthened if followed by a reinforcer or diminished if followed by a punisher. Focuses on <strong>consequences</strong>.</p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Key Figures</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Edward Thorndike" 
                definition="Law of Effect—behaviors followed by favorable consequences become more likely."
              />
              <KeyTerm 
                term="B.F. Skinner" 
                definition="Developed the 'Skinner Box' (Operant Chamber); coined the term operant conditioning."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Reinforcement (Increases Behavior)</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Positive Reinforcement</h4>
                <p className="text-sm text-slate-700 mt-2"><em>Adding</em> a desirable stimulus (e.g., giving a dog a treat).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Negative Reinforcement</h4>
                <p className="text-sm text-slate-700 mt-2"><em>Removing</em> an aversive stimulus (e.g., taking an aspirin to remove a headache).</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Note: Negative reinforcement is GOOD for the learner.</strong></p>
              </div>
              <KeyTerm 
                term="Primary Reinforcer" 
                definition="Innately satisfying (food, water)."
              />
              <KeyTerm 
                term="Secondary (Conditioned) Reinforcer" 
                definition="Learned association (money, grades)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Punishment (Decreases Behavior)</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Positive Punishment</h4>
                <p className="text-sm text-slate-700 mt-2"><em>Adding</em> an aversive stimulus (e.g., spanking, traffic ticket).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Negative Punishment</h4>
                <p className="text-sm text-slate-700 mt-2"><em>Removing</em> a rewarding stimulus (e.g., taking away phone, timeout).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Reinforcement Schedules</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Continuous" 
                definition="Reinforce every time. Fast learning, fast extinction."
              />
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Partial (Intermittent)</h4>
                <p className="text-sm text-slate-700 mt-2">Reinforce only sometimes. Slower learning, greater resistance to extinction.</p>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Fixed-Ratio (FR)</p>
                    <p className="text-xs text-slate-600">Reward after set <em>number</em> of responses (e.g., buy 10 coffees, get 1 free).</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Variable-Ratio (VR) ⭐</p>
                    <p className="text-xs text-slate-600">Reward after unpredictable <em>number</em> of responses (e.g., slot machines). <em>Most resistant to extinction.</em></p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Fixed-Interval (FI)</p>
                    <p className="text-xs text-slate-600">Reward after set <em>time</em> elapses (e.g., paycheck every 2 weeks).</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Variable-Interval (VI)</p>
                    <p className="text-xs text-slate-600">Reward after unpredictable <em>time</em> elapses (e.g., checking email).</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Shaping</h3>
            <KeyTerm 
              term="Shaping" 
              definition="Reinforcing successive approximations toward a desired behavior (e.g., training a dolphin to jump through a hoop by rewarding small steps)."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-3-9",
      title: "Topic 3.9: Social, Cognitive, and Neurological Factors in Learning",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Social Learning Theory (Observational Learning)</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Albert Bandura</h4>
                <p className="text-sm text-slate-700 mt-2">Famous <strong>Bobo Doll Experiment</strong>. Demonstrated that children learn aggression by observing an adult model.</p>
              </div>
              <KeyTerm 
                term="Modeling" 
                definition="The process of observing and imitating a specific behavior."
              />
              <KeyTerm 
                term="Vicarious Reinforcement/Punishment" 
                definition="Learning by seeing others rewarded or punished."
              />
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Mirror Neurons</h4>
                <p className="text-sm text-slate-700 mt-2">Frontal lobe neurons that fire when performing certain actions <em>or</em> when observing another doing so. The neural basis for imitation and empathy.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Cognitive Factors in Learning</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Behaviorists (Skinner/Watson) ignored mental processes, but cognitive psychologists emphasized them.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Latent Learning (Edward Tolman)</h4>
                <p className="text-sm text-slate-700 mt-2">Learning that occurs but is not apparent until there is an incentive to demonstrate it. (Rats formed a <strong>Cognitive Map</strong> of a maze without reinforcement).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Insight Learning (Wolfgang Köhler)</h4>
                <p className="text-sm text-slate-700 mt-2">A sudden realization of a problem's solution (Chimps stacking boxes to reach bananas).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Learned Helplessness (Martin Seligman)</h4>
                <p className="text-sm text-slate-700 mt-2">The hopelessness and passive resignation an animal or human learns when unable to avoid repeated aversive events. (Linked to depression; internal locus of control protects against this).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Coping Mechanisms</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Problem-Focused Coping" 
                definition="Attempting to alleviate stress directly by changing the stressor."
              />
              <KeyTerm 
                term="Emotion-Focused Coping" 
                definition="Attempting to alleviate stress by avoiding or ignoring a stressor and attending to emotional needs."
              />
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Locus of Control</h4>
                <div className="mt-2 space-y-2">
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">Internal</p>
                    <p className="text-xs text-slate-600">You control your own fate.</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-sm font-semibold text-slate-800">External</p>
                    <p className="text-xs text-slate-600">Chance or outside forces determine your fate.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default psychologyUnit3Notes;